import { HelmetDexteel } from "@dexteel/mesf-core";
import { Grid2 as Grid, ThemeProvider } from "@mui/material";
import { GridApi, GridReadyEvent, themeMaterial } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	ALTO,
	YELLOW_PENDING_ROW,
} from "../../../../controls/shared/constants/colors";
import { DeliveryTicketModal } from "../../caster-data-entry-v2/components/modals/delivery-ticket/DeliveryTicketModal";
import dataEntries from "../../caster-data-entry-v2/styles/useDataEntryStyles";
import { ProductionDataRow } from "../models/ProductionDataModel";
import { useTableData } from "./hooks/useTableData";

type Props = {
	isLoading: boolean;
	getProductionData: Function;
	gridRef: React.RefObject<AgGridReact>;
	rows: ProductionDataRow[];
};

export const ProductionManagementTable = ({
	isLoading,
	getProductionData,
	gridRef,
	rows,
}: Props) => {
	const [modalDeliveryTicket, setModalDeliveryTicket] = useState(false);
	const [selectedProcessId, setSelectedProcessId] = useState<number | null>(
		null,
	);
	const [selectedProcessData, setSelectedProcessData] = useState<any>(null);
	const [selectedProcessLadleClose, setSelectedProcessLadleClose] =
		useState<Date | null>(null);
	const [gridAPI, setGridAPI] = useState<GridApi | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();

	const handleDeclareClick = (processId: number, ladleClose?: Date | null) => {
		setSelectedProcessId(processId);
		setSelectedProcessLadleClose(ladleClose || null);
		setModalDeliveryTicket(true);
	};

	const { columnData, showContextMenu } = useTableData({
		onDeclareClick: handleDeclareClick,
	});

	const handleContainerContextMenu = (
		event: React.MouseEvent<HTMLDivElement, MouseEvent>,
	) => {
		event.preventDefault();
	};

	const getRowStyle = (params: any) => {
		// Only Pending rows get yellow background (not declared and no pourback)
		if (!params.data.Declared && !params.data.HasPourback) {
			return { background: YELLOW_PENDING_ROW };
		}
		// Pourback and Declared - default/white background
	};

	const onGridReady = (params: GridReadyEvent) => {
		setGridAPI(params.api);
	};

	const onSelectionChanged = () => {
		if (!gridAPI) return;
		const selectedRows = gridAPI.getSelectedRows();
		if (selectedRows.length > 0) {
			setSelectedProcessData(selectedRows[0]);
			setSearchParams(
				{ processId: selectedRows[0].ProcessId },
				{ replace: true },
			);
		} else {
			setSelectedProcessData(null);
			setSearchParams({}, { replace: true });
		}
	};

	const processIdSearch = searchParams.get("processId");
	const openModalParam = searchParams.get("openModal");

	useEffect(() => {
		if (gridAPI) {
			if (isLoading) gridAPI.showLoadingOverlay();
			else gridAPI.hideOverlay();
		}
	}, [gridAPI, isLoading]);

	useEffect(() => {
		if (!isLoading && gridAPI && rows && rows.length > 0) {
			const processId = processIdSearch;

			if (processId) {
				const node = gridAPI.getRowNode(processId);
				if (node) {
					gridAPI.deselectAll();
					node?.setSelected(true, true);
					setSelectedProcessData(node.data);

					// If openModal parameter is present, open the delivery ticket modal
					if (openModalParam === "deliveryTicket") {
						setSelectedProcessId(Number(processId));
						setSelectedProcessLadleClose(node.data.LadleClose || null);
						setModalDeliveryTicket(true);
						// Remove the openModal parameter from URL
						searchParams.delete("openModal");
						setSearchParams(searchParams, { replace: true });
					}
				}
			}
		}
	}, [gridAPI, isLoading, rows, openModalParam]);

	return (
		<>
			{selectedProcessData && !modalDeliveryTicket && (
				<HelmetDexteel
					title={`${selectedProcessData.HeatName} - Production Management`}
				/>
			)}
			<div onContextMenu={handleContainerContextMenu}>
				<Grid container>
					<Grid
						size={{ xs: 12, md: 12 }}
						style={{
							border: `1px solid ${ALTO}`,
							minHeight: "65vh",
							height: "65vh",
						}}
						className="ag-theme-material"
					>
						<ThemeProvider theme={dataEntries}>
							<DeliveryTicketModal
								show={modalDeliveryTicket}
								onHide={(shouldUpdate: boolean) => {
									setModalDeliveryTicket(false);
									if (shouldUpdate) getProductionData().then(() => {});
								}}
								processId={selectedProcessId as number}
								ladleClose={selectedProcessLadleClose}
							/>
						</ThemeProvider>
						<AgGridReact
							onGridReady={onGridReady}
							rowData={rows}
							columnDefs={columnData}
							rowSelection="single"
							overlayLoadingTemplate={"Loading..."}
							suppressContextMenu={true}
							getRowId={(params) => params.data.ProcessId}
							getRowStyle={getRowStyle}
							onRowDoubleClicked={(event: any) => {
								handleDeclareClick(event.data.ProcessId, event.data.LadleClose);
							}}
							onCellContextMenu={(event) => {
								event.api.deselectAll();
								event.node.setSelected(true);
								setSelectedProcessData(event.data);
								if (event.event)
									showContextMenu(
										event.event as any,
										event.data,
										"production-management",
									);
							}}
							onSelectionChanged={onSelectionChanged}
							ref={gridRef}
						/>
					</Grid>
				</Grid>
			</div>
		</>
	);
};
